// src/services/talentAnalysis.ts

// 定义请求体中才干数据的接口
export interface TalentData {
  top10: string[];
  bottom5: string[];
  // 团队专属提示词（从后端匹配出的公司提示词）
  prompt?: string;
  // 用户填写的岗位，用于在报告中个性化描述
  position?: string;
}

// 前端流式回调（与情绪记忆页一致的接口）
export type StreamCallbacks = {
  onStart?: () => void;
  onToken?: (token: string) => void;
  onComplete?: (fullText: string) => void;
  onError?: (err: any) => void;
};

// 声明环境变量类型
declare global {
  namespace NodeJS {
    interface ProcessEnv {
      REACT_APP_ARK_API_KEY: string;
    }
  }
}

// 仅保留 Doubao（Ark）通道
const DOUBAO_API_ENDPOINT = '/api/chat';

// 生成才干分析报告：支持流式与非流式（是否传入 callbacks 决定）
export async function generateTalentAnalysisReport(
  data: TalentData,
  callbacks?: StreamCallbacks
): Promise<string> {
  try {
    const top10Text = data.top10.join('、');
    const bottom5Text = data.bottom5.join('、');

    // 仅使用后端提供的系统提示词（data.prompt）。不再内置任何默认系统提示词。
    const userPrompt = `${data.position ? `我的岗位是：【${data.position}】；` : ''}我的才干测评数据：前10个优势才干为：${top10Text}；后5个弱势才干为：${bottom5Text}。`;

    // 仅使用 Doubao（Ark）端点
    const endpoint = DOUBAO_API_ENDPOINT;
    console.log(`正在发送请求到才干分析API... provider=doubao, endpoint=${endpoint}, stream=${Boolean(callbacks)}`);

    // 组装不同提供方的请求体
    const arkBody = {
      model: typeof window !== 'undefined'
        ? (new URLSearchParams(window.location.search).get('model') || 'doubao-seed-2-0-pro-260215')
        : 'doubao-seed-2-0-pro-260215',
      reasoning_effort: 'minimal',
      messages: [
        ...(data.prompt ? [{ role: 'system', content: data.prompt }] : []),
        { role: 'user', content: userPrompt }
      ],
      temperature: 0.65,
      stream: Boolean(callbacks),
      max_tokens: 3200
    } as any;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(callbacks ? { 'Accept': 'text/event-stream' } : {})
      },
      body: JSON.stringify(arkBody)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API请求失败: ${response.status} ${response.statusText} - ${errorText}`);
    }

    // 流式处理：逐块解析 SSE 数据
    if (callbacks) {
      callbacks.onStart && callbacks.onStart();

      const reader = response.body?.getReader();
      const decoder = new TextDecoder('utf-8');
      if (!reader) {
        throw new Error('浏览器不支持可读流');
      }

      let bufferedText = '';
      let fullText = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          bufferedText += chunk;

          // SSE 通常按行分隔，逐行处理 data: xxxx
          const lines = bufferedText.split(/\r?\n/);
          // 保留最后一行（可能是不完整的下一条数据）
          bufferedText = lines.pop() ?? '';

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            if (!trimmed.startsWith('data:')) continue;

            const payload = trimmed.substring(5).trim();
            if (!payload) continue;
            if (payload === '[DONE]' || payload === 'DONE') {
              // 流结束
              callbacks.onComplete && callbacks.onComplete(fullText);
              return fullText;
            }
            try {
              const parsed = JSON.parse(payload);
              // Doubao/Ark 流式：choices[0].delta.content
              const token = parsed?.choices?.[0]?.delta?.content
                ?? parsed?.choices?.[0]?.message?.content
                ?? parsed?.output_text
                ?? '';
              if (token) {
                fullText += token;
                callbacks.onToken && callbacks.onToken(token);
              }
            } catch (e) {
              // 非 JSON 行，忽略
              // console.debug('忽略无法解析的流数据行:', payload);
            }
          }
        }

        // 读完但未显式收到 [DONE]
        callbacks.onComplete && callbacks.onComplete(fullText);
        return fullText;
      } catch (e) {
        callbacks.onError && callbacks.onError(e);
        throw e;
      }
    }

    // 非流式处理：一次性解析完整响应（按提供方解析）
    const result = await response.json();
    const fullText: string = result?.choices?.[0]?.message?.content || '';
    return fullText;
  } catch (error) {
    console.error('生成才干分析报告失败:', error);
    // 在Promise拒绝时也返回错误信息
    return Promise.reject(error);
  }
}
